import { type FC, type ReactElement, useState, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { useLazyLoadMedia } from 'dibs-elements/exports/useLazyLoadMedia.tsx';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations.ts';
import { Carousel } from 'dibs-carousel';
import { CarouselDots } from 'dibs-carousel/exports/CarouselDots.jsx';
import { type NftPdpAdditionalContent_nftItem$key } from './__generated__/NftPdpAdditionalContent_nftItem.graphql.ts';

import styles from './NftPdpAdditionalContent.scss';

type Props = {
    nftItem: NftPdpAdditionalContent_nftItem$key;
};

const counter = (index: number, totalItems: number): ReactElement => (
    <FormattedMessage
        id="nft.pdp.additionalContent.carouselCount"
        defaultMessage="{currentIndex} of {totalItems}"
        values={{ currentIndex: index + 1, totalItems }}
    />
);

const MAX_MEDIA_SIZE = '714';

const imgParams = ioParams({
    height: MAX_MEDIA_SIZE,
    width: MAX_MEDIA_SIZE,
    fit: 'bounds',
    'resize-filter': 'bilinear',
    format: 'pjpg',
    disable: 'upscale',
});

const staticImgString = ioParams(imgParams, { dpr: '2', auto: 'webp', optimize: 'high' }).toQuery();

const AdditionalContentImage: FC<{
    url?: string | null;
    height?: string;
    width?: string;
}> = ({ url, height, width }) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const lazyLoad = useLazyLoadMedia('image', {
        ref: imgRef,
        width,
        height,
        maxHeight: '481px',
        maxWidth: `${width}px`,
        shimmer: false,
    });
    return (
        <img
            alt=""
            ref={imgRef}
            className={lazyLoad.styleProps.className}
            style={lazyLoad.styleProps.style}
            width={width}
            height={height}
            src={url + staticImgString}
            loading="eager"
        />
    );
};

export const NftPdpAdditionalContent: FC<Props> = ({ nftItem: nftItemRef }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const nftItem = useFragment(
        graphql`
            fragment NftPdpAdditionalContent_nftItem on NftItem {
                additionalInfo {
                    copy
                    videoEmbed
                    videoEmbedCaption
                    imageList {
                        url
                        height
                        width
                    }
                    imageListCaption
                }
            }
        `,
        nftItemRef
    );
    const { additionalInfo } = nftItem || {};
    if (!additionalInfo || !additionalInfo?.copy) {
        return null;
    }
    const { copy, videoEmbed, videoEmbedCaption, imageList, imageListCaption } = additionalInfo;
    let markup = copy;
    if (videoEmbed) {
        const videoWrapper = (
            <figure className={styles.videoWrapper}>
                <div
                    className={styles.iframeWrapper}
                    dangerouslySetInnerHTML={{ __html: videoEmbed }}
                />
                {videoEmbedCaption && (
                    <figcaption className={`${styles.caption} ${styles.videoCaption}`}>
                        {videoEmbedCaption}
                    </figcaption>
                )}
            </figure>
        );
        markup = markup.replace('{video}', renderToStaticMarkup(videoWrapper));
    }
    let bodyText: ReactElement;
    if (imageList?.length) {
        const totalItems = imageList.length;
        const carousel = (
            <figure className={styles.carouselWrapper}>
                <div className={styles.hideOnMobile}>
                    <Carousel
                        totalItems={totalItems}
                        itemsPerPage={1}
                        step={1}
                        onIndexChange={({ index }: { index: number }) => setCurrentIndex(index)}
                        renderItem={({ index }: { index: number }) => {
                            const { url, width, height } = imageList[index] || {};
                            return (
                                <div
                                    key={`additional-image-${url}-${index}`}
                                    className={styles.imageWrapper}
                                >
                                    <AdditionalContentImage
                                        url={url}
                                        width={width || undefined}
                                        height={height || undefined}
                                    />
                                </div>
                            );
                        }}
                    />
                    {totalItems > 1 && (
                        <div className={styles.counter}>{counter(currentIndex, totalItems)}</div>
                    )}
                </div>
                <div className={styles.showOnMobile}>
                    <Swiper
                        itemsPerPage={1}
                        hasInfiniteLoop
                        onPageChange={(newPhoto: number) => setCurrentIndex(newPhoto)}
                        startingPage={currentIndex}
                        classNames={{ wrapper: styles.swiperCarousel, item: styles.listItem }}
                    >
                        {imageList.map((image, index) => {
                            const { url, width, height } = image || {};
                            return (
                                <div
                                    key={`additional-image-${url}-${index}`}
                                    className={styles.imageWrapper}
                                >
                                    <AdditionalContentImage
                                        url={url}
                                        width={width || undefined}
                                        height={height || undefined}
                                    />
                                </div>
                            );
                        })}
                    </Swiper>
                    {totalItems > 1 && (
                        <CarouselDots
                            dotsPerPage={6}
                            index={currentIndex}
                            totalItems={totalItems}
                        />
                    )}
                </div>
                {imageListCaption && (
                    <figcaption className={styles.caption}>{imageListCaption}</figcaption>
                )}
            </figure>
        );
        // this solution relies on the assumuption that there will only ever be one carousel in the markup
        const splitMarkup = markup.split('{carousel}');
        bodyText = (
            <div className={styles.bodyText}>
                <div dangerouslySetInnerHTML={{ __html: splitMarkup[0] }} />
                {carousel}
                <div dangerouslySetInnerHTML={{ __html: splitMarkup[1] }} />
            </div>
        );
    } else {
        bodyText = <div className={styles.bodyText} dangerouslySetInnerHTML={{ __html: markup }} />;
    }
    return (
        <div className={styles.wrapper}>
            <HeadingLevel>
                {Heading => (
                    <div className={styles.body}>
                        <Heading className={styles.header}>
                            <FormattedMessage
                                id="nft.pdp.additionalContent.header"
                                defaultMessage="Exhibition Notes"
                            />
                        </Heading>
                        {bodyText}
                    </div>
                )}
            </HeadingLevel>
            <div className={styles.mobileSeparatorContainer}>
                <div className={styles.mobileSeparator} />
            </div>
        </div>
    );
};
